import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { PricingTypeSelector } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricingTypeSelector"
import { LocalPricingTable } from "../LocalPricingTable/LocalPricingTable"
import { Button, Flex, Form, Typography } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { Icon } from "ui/Icon"
import { spacings } from "themes/variables"
import { generateUUID } from "components/trips/utils"
import { PRICING_TYPES } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { useDispatch, useSelector } from "react-redux"
import { errorFieldsSelector } from "modules/errors/selectors"
import { CustomInputError } from "ui/CustomInputError"
import { receiveErrors } from "modules/errors/reducer"
import { AddBookingOptionModal } from "../../pricing/AddBookingOptionModal/AddBookingOptionModal"
import PropTypes from "prop-types"

const { Text } = Typography

export const LengthAndRates = ({
  form,
  isShowErrorMessage,
  setIsShowErrorMessage,
  localPrices,
  onPriceSave,
  onPriceDelete,
  expandedRowData,
  setExpandedRowData,
  cardTitle,
  addButtonLabel,
  bookingOptionsTitle,
  bookingOptionsSubtitle,
}) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const pricingType = Form.useWatch("pricing_type", form) || PRICING_TYPES.PER_PERSON
  const [draftPrices, setDraftPrices] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalRecord, setModalRecord] = useState(null)

  const errors = useSelector(errorFieldsSelector())

  const resolvedCardTitle = cardTitle || t("experiences.scheduling.sections.length_and_rates.title")
  const resolvedAddLabel = addButtonLabel || t("experiences.scheduling.sections.length_and_rates.add_new")
  const isWizardLayout = !!bookingOptionsTitle

  const openModal = (record = null) => {
    setModalRecord(record)
    setIsModalOpen(true)
    dispatch(receiveErrors({ data: { message: { listing_media: [] } }, showToast: false }))
  }

  const addNewPrice = () => {
    if (isWizardLayout) {
      openModal(null)
      return
    }
    setDraftPrices((prev) => [...prev, { id: generateUUID() }])
    setExpandedRowData(null)
    dispatch(
      receiveErrors({
        data: { message: { listing_media: [] } },
        showToast: false
      })
    )
  }

  // Prices to display: locally managed, excluding destroyed entries
  const displayPrices = localPrices.filter((p) => !p._destroy)

  return (
    <Card title={resolvedCardTitle} className="mb-3xl">
      <Flex vertical gap={spacings.XL}>
        {isWizardLayout ? (
          <>
            <PricingTypeSelector
              value={pricingType}
              setPricingType={(val) => form.setFieldValue("pricing_type", val)}
              form={form}
              setIsShowErrorMessage={setIsShowErrorMessage}
            />
            <Flex align="center" justify="space-between">
              <Text strong>{bookingOptionsTitle}</Text>
              <Button color="primary" variant="solid" size="large" onClick={addNewPrice}>
                <Icon type="feather" name="Plus" color="currentColor" />
                {resolvedAddLabel}
              </Button>
            </Flex>
            {bookingOptionsSubtitle && (
              <Flex align="center" gap={spacings.XS}>
                <InfoCircleOutlined />
                <Text type="secondary">{bookingOptionsSubtitle}</Text>
              </Flex>
            )}
          </>
        ) : (
          <Flex align="center" justify="space-between" className="gap-3 lengthRatewrapper flex-wrap">
            <PricingTypeSelector
              value={pricingType}
              setPricingType={(val) => form.setFieldValue("pricing_type", val)}
              form={form}
              setIsShowErrorMessage={setIsShowErrorMessage}
            />
            <Button color="primary" variant="solid" size="large" onClick={addNewPrice}>
              <Icon type="feather" name="Plus" color="currentColor" />
              {resolvedAddLabel}
            </Button>
          </Flex>
        )}
        <LocalPricingTable
          prices={displayPrices}
          draftPrices={draftPrices}
          setDraftPrices={setDraftPrices}
          addNewPrice={addNewPrice}
          setExpandedRowData={setExpandedRowData}
          expandedRowData={expandedRowData}
          setPricingType={(val) => form.setFieldValue("pricing_type", val)}
          pricingType={pricingType}
          form={form}
          isFlatRate={pricingType === PRICING_TYPES.FLAT_RATE}
          onPriceSave={onPriceSave}
          onPriceDelete={onPriceDelete}
          isWizardLayout={isWizardLayout}
          onEditPrice={isWizardLayout ? openModal : undefined}
        />
        {!isWizardLayout && (
          <Button color="primary" variant="link" size="large" className="p-0 mr-auto" onClick={addNewPrice}>
            <Icon type="feather" name="Plus" color="currentColor" />
            {resolvedAddLabel}
          </Button>
        )}
        {isShowErrorMessage && errors.listing_rates && <CustomInputError errorKey={errors.listing_rates[0]} />}
      </Flex>
      {isWizardLayout && (
        <AddBookingOptionModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          record={modalRecord}
          wizardForm={form}
          isFlatRate={pricingType === PRICING_TYPES.FLAT_RATE}
          onPriceSave={onPriceSave}
        />
      )}
    </Card>
  )
}

LengthAndRates.propTypes = {
  form: PropTypes.object.isRequired,
  isShowErrorMessage: PropTypes.bool.isRequired,
  setIsShowErrorMessage: PropTypes.func.isRequired,
  localPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPriceSave: PropTypes.func.isRequired,
  onPriceDelete: PropTypes.func.isRequired,
  expandedRowData: PropTypes.object,
  setExpandedRowData: PropTypes.func.isRequired,
  cardTitle: PropTypes.string,
  addButtonLabel: PropTypes.string,
  bookingOptionsTitle: PropTypes.string,
  bookingOptionsSubtitle: PropTypes.string,
}
